import { useAppData } from "@/data/AppDataContext";
import { useDataIsolation } from "@/hooks/useDataIsolation";
import { IssueService } from "@/services/issue.service";
import { PermissionGate } from "@/components/PermissionGate";
import { Card, CardContent } from "@/components/ui/card";
import { getIssueSuggestedActions, formatRelativeTime } from "@/data/suggestedActions";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import type { Issue, IssueStatus } from "@/data/types";
import { DollarSign as CreditIcon } from "lucide-react";
import { CreateCreditForm } from "@/components/CreateCreditForm";
import type { CustomerCredit } from "@/data/types";
import {
  AlertTriangle, Search, MapPin, User, Calendar,
  ExternalLink, Clock, CheckCircle2, AlertCircle, XCircle,
  MessageSquare, ChevronRight, Briefcase, Users, Wrench,
  FileWarning, Shield, ArrowRight, CircleDot, Archive,
  ShieldAlert, ArrowUpCircle, Mail, Zap, Package, ClipboardCheck,
} from "lucide-react";
import { BranchRegionFilter, EMPTY_BR, type BranchRegionState } from "@/components/BranchRegionFilter";
import { QuickViews, type QuickView } from "@/components/QuickViews";
import { formatAddress } from "@/lib/addressUtils";

const SEVERITY_ICONS: Record<string, React.ElementType> = {
  Urgent: AlertTriangle,
  Medium: AlertCircle,
  Low: Clock,
};

const STATUS_OPTIONS: IssueStatus[] = ["Open", "In Progress", "Resolved", "Closed"];

const STATUS_META: Record<IssueStatus, { icon: React.ElementType; color: string; label: string }> = {
  Open: { icon: AlertCircle, color: "text-warning", label: "Opened" },
  "In Progress": { icon: Wrench, color: "text-info", label: "Investigation Started" },
  Resolved: { icon: CheckCircle2, color: "text-success", label: "Resolved" },
  Closed: { icon: Archive, color: "text-muted-foreground", label: "Closed" },
};

function daysAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - d.getTime()) / 86400000));
}

export default function IssuesList() {
  const { issueId } = useParams<{ issueId?: string }>();
  const { setIssues, contractors, credits, setCredits } = useAppData();
  const { issues, jobs } = useDataIsolation();
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [filter, setFilter] = useState<string>("open");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("All");
  const [severityFilter, setSeverityFilter] = useState<string>("All");
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);

  // Load real issues from Supabase and sync into context on mount
  useEffect(() => {
    IssueService.getAll().then(({ data }) => {
      if (data) setIssues(data);
    });
  }, []);

  useEffect(() => {
    if (!issueId || issues.length === 0) return;
    const match = issues.find((i) => i.id === issueId);
    if (match) setSelectedIssue(match);
  }, [issueId, issues]);
  const [newNote, setNewNote] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);

  // Issues returned from the service have jobId replaced with job_number (e.g. "JOB-001"),
  // not UUID — so we need both maps for lookups.
  const jobMap = new Map([
    ...jobs.map((j) => [j.id, j] as [string, typeof j]),
    ...jobs.map((j) => [j.jobNumber as string, j] as [string, typeof j]),
  ]);
  const contractorMap = new Map(contractors.map((c) => [c.id, c]));

  const issueTypes = [...new Set(issues.map((i) => i.issueType))].sort();

  const filtered = issues.filter((i) => {
    const job = jobMap.get(i.jobId);
    if (brFilter.branch !== "all" && job?.branch !== brFilter.branch) return false;
    if (brFilter.region !== "all" && job?.region !== brFilter.region) return false;
    const matchesView = (() => {
      switch (filter) {
        case "open": return i.status === "Open" || i.status === "In Progress";
        case "resolved": return i.status === "Resolved" || i.status === "Closed";
        case "urgent": return i.severity === "Urgent" && (i.status === "Open" || i.status === "In Progress");
        case "escalated": return isEscalated(i);
        case "crew": return job?.assignmentType === "Internal Crew" && (i.status === "Open" || i.status === "In Progress");
        case "sub": return job?.assignmentType === "Subcontractor" && (i.status === "Open" || i.status === "In Progress");
        case "customer": return i.issueType === "Customer Complaint" && (i.status === "Open" || i.status === "In Progress");
        case "install": return i.issueType === "Install Issue" && (i.status === "Open" || i.status === "In Progress");
        case "material": return (i.issueType === "Material Issue" || i.issueType === "Ordering Issue") && (i.status === "Open" || i.status === "In Progress");
        default: return true;
      }
    })();
    const matchesSearch =
      !search ||
      i.id.toLowerCase().includes(search.toLowerCase()) ||
      i.description.toLowerCase().includes(search.toLowerCase()) ||
      i.owner.toLowerCase().includes(search.toLowerCase()) ||
      i.jobId.toLowerCase().includes(search.toLowerCase()) ||
      (job?.customerName.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (job ? formatAddress(job.address).toLowerCase().includes(search.toLowerCase()) : false);
    const matchesType = typeFilter === "All" || i.issueType === typeFilter;
    const matchesSeverity = severityFilter === "All" || i.severity === severityFilter;
    return matchesView && matchesSearch && matchesType && matchesSeverity;
  });

  const sorted = [...filtered].sort((a, b) => {
    const sev: Record<string, number> = { Urgent: 0, Medium: 1, Low: 2 };
    const statusOrd: Record<string, number> = { Open: 0, "In Progress": 1, Resolved: 2, Closed: 3 };
    if (statusOrd[a.status] !== statusOrd[b.status]) return statusOrd[a.status] - statusOrd[b.status];
    return sev[a.severity] - sev[b.severity];
  });

  const openCount = issues.filter((i) => i.status === "Open" || i.status === "In Progress").length;
  const urgentCount = issues.filter((i) => i.severity === "Urgent" && (i.status === "Open" || i.status === "In Progress")).length;
  const inProgressCount = issues.filter((i) => i.status === "In Progress").length;
  const resolvedCount = issues.filter((i) => i.status === "Resolved" || i.status === "Closed").length;
  const crewIssues = issues.filter((i) => (i.status === "Open" || i.status === "In Progress") && jobMap.get(i.jobId)?.assignmentType === "Internal Crew").length;
  const subIssues = issues.filter((i) => (i.status === "Open" || i.status === "In Progress") && jobMap.get(i.jobId)?.assignmentType === "Subcontractor").length;
  const customerComplaints = issues.filter((i) => i.issueType === "Customer Complaint" && (i.status === "Open" || i.status === "In Progress")).length;
  const installIssues = issues.filter((i) => i.issueType === "Install Issue" && (i.status === "Open" || i.status === "In Progress")).length;
  const materialIssues = issues.filter((i) => (i.issueType === "Material Issue" || i.issueType === "Ordering Issue") && (i.status === "Open" || i.status === "In Progress")).length;
  const escalatedCount = issues.filter((i) => (i.status === "Open" || i.status === "In Progress") && daysAgo(i.dateCreated) >= 3 && i.severity === "Urgent").length;

  const isEscalated = (issue: Issue) =>
    (issue.status === "Open" || issue.status === "In Progress") && daysAgo(issue.dateCreated) >= 3 && issue.severity === "Urgent";

  const quickViews: QuickView[] = [
    { key: "all", label: "All", count: issues.length },
    { key: "open", label: "Open", count: openCount, accent: "warning" },
    { key: "urgent", label: "Urgent", count: urgentCount, accent: "destructive" },
    { key: "escalated", label: "Escalated", count: escalatedCount, accent: "destructive" },
    { key: "crew", label: "Crew Issues", count: crewIssues, accent: "info" },
    { key: "sub", label: "Sub Issues", count: subIssues, accent: "warning" },
    { key: "customer", label: "Customer Complaints", count: customerComplaints, accent: "destructive" },
    { key: "install", label: "Install Issues", count: installIssues, accent: "warning" },
    { key: "material", label: "Material / Order", count: materialIssues, accent: "warning" },
    { key: "resolved", label: "Resolved", count: resolvedCount, accent: "success" },
  ];

  const today = new Date().toISOString().split("T")[0];

  const applyStatusDates = (issue: Issue, newStatus: IssueStatus): Issue => {
    const updated = { ...issue, status: newStatus };
    if (newStatus === "In Progress" && !issue.dateInProgress) updated.dateInProgress = today;
    if (newStatus === "Resolved" && !issue.dateResolved) {
      updated.dateResolved = today;
      if (!issue.dateInProgress) updated.dateInProgress = today;
    }
    if (newStatus === "Closed" && !issue.dateClosed) {
      updated.dateClosed = today;
      if (!issue.dateResolved) updated.dateResolved = today;
      if (!issue.dateInProgress) updated.dateInProgress = today;
    }
    return updated;
  };

  const handleStatusChange = async (issueId: string, newStatus: IssueStatus) => {
    // Optimistic update — update context immediately so the UI reflects the change
    let updatedIssue: Issue | null = null;
    setIssues((prev) =>
      prev.map((i) => {
        if (i.id !== issueId) return i;
        const u = applyStatusDates(i, newStatus);
        updatedIssue = u;
        return u;
      })
    );
    if (selectedIssue?.id === issueId) {
      setSelectedIssue((prev) => {
        if (!prev) return null;
        const u = applyStatusDates(prev, newStatus);
        updatedIssue = u;
        return u;
      });
    }

    // Persist to DB — pass the full updated issue so metadata fields are preserved
    if (updatedIssue) {
      const { error } = await IssueService.update(issueId, updatedIssue);
      if (error) {
        console.error("[IssuesList] Status update failed, reverting:", error);
        // Revert on failure
        setIssues((prev) => prev.map((i) => (i.id === issueId ? { ...i, status: selectedIssue?.status ?? i.status } : i)));
      }
    }
  };

  const handleSaveResolutionNotes = async () => {
    if (!selectedIssue || !resolutionNotes.trim()) return;
    const notes = resolutionNotes.trim();
    setIssues((prev) =>
      prev.map((i) => (i.id === selectedIssue.id ? { ...i, resolutionNotes: notes } : i))
    );
    setSelectedIssue((prev) => (prev ? { ...prev, resolutionNotes: notes } : null));
    setResolutionNotes("");
    await IssueService.update(selectedIssue.id, { ...selectedIssue, resolutionNotes: notes });
  };

  const selectedJob = selectedIssue ? jobMap.get(selectedIssue.jobId) : null;
  const selectedContractor = selectedIssue?.relatedContractorId
    ? contractorMap.get(selectedIssue.relatedContractorId)
    : null;

  // Build lifecycle timeline entries for selected issue
  const buildTimeline = (issue: Issue) => {
    const entries: { date: string; label: string; icon: React.ElementType; color: string; detail?: string }[] = [];
    entries.push({
      date: issue.dateCreated,
      label: "Issue Reported",
      icon: AlertCircle,
      color: "text-warning",
      detail: `${issue.issueType} · Source: ${issue.source}`,
    });
    if (issue.dateInProgress) {
      entries.push({
        date: issue.dateInProgress,
        label: "Investigation Started",
        icon: Wrench,
        color: "text-info",
        detail: `Assigned to ${issue.owner}`,
      });
    }
    if (issue.dateResolved) {
      entries.push({
        date: issue.dateResolved,
        label: "Issue Resolved",
        icon: CheckCircle2,
        color: "text-success",
        detail: issue.resolutionNotes || undefined,
      });
    }
    if (issue.dateClosed) {
      entries.push({
        date: issue.dateClosed,
        label: "Issue Closed",
        icon: Archive,
        color: "text-muted-foreground",
      });
    }
    return entries;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Issue Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Service resolution and quality control workflow
          </p>
        </div>
        <Link to="/issue-form">
          <PermissionGate permission="issues_create">
            <Button className="gap-2">
              <FileWarning className="h-4 w-4" />
              Report New Issue
            </Button>
          </PermissionGate>
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Open Issues", value: openCount, icon: AlertCircle, accent: "text-warning" },
          { label: "Urgent", value: urgentCount, icon: AlertTriangle, accent: "text-destructive" },
          { label: "Escalated", value: escalatedCount, icon: ShieldAlert, accent: "text-destructive" },
          { label: "In Progress", value: inProgressCount, icon: Wrench, accent: "text-info" },
          { label: "Resolved", value: resolvedCount, icon: CheckCircle2, accent: "text-success" },
        ].map((stat) => (
          <Card key={stat.label} className="shadow-none">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-muted ${stat.accent}`}>
                <stat.icon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <QuickViews views={quickViews} active={filter} onChange={setFilter} />
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search issues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-8 text-xs w-[200px]"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 text-xs w-[150px]">
              <SelectValue placeholder="Issue Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Types</SelectItem>
              {issueTypes.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="h-8 text-xs w-[120px]">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Severity</SelectItem>
              <SelectItem value="Urgent">Urgent</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
            </SelectContent>
          </Select>
          <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={jobs} compact />
        </div>
      </div>

      {/* Issues Table */}
      <Card className="shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Issue</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Customer / Job</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden lg:table-cell">Location</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Type</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Severity</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Owner</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Status</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Age</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((issue) => {
                  const job = jobMap.get(issue.jobId);
                  const SevIcon = SEVERITY_ICONS[issue.severity] || Clock;
                  const age = daysAgo(issue.dateCreated);
                  return (
                    <tr
                      key={issue.id}
                      className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedIssue(issue);
                        setResolutionNotes(issue.resolutionNotes || "");
                      }}
                    >
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-foreground">{issue.id}</span>
                          {isEscalated(issue) && (
                            <span className="inline-flex items-center gap-0.5 rounded bg-destructive/10 border border-destructive/20 px-1.5 py-0.5 text-[9px] font-semibold text-destructive" title="Escalated — urgent issue open 3+ days, email notification queued">
                              <ShieldAlert className="h-2.5 w-2.5" /> ESCALATED
                              <Mail className="h-2.5 w-2.5 ml-0.5 opacity-60" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div>
                          <Link
                            to={`/customers/${encodeURIComponent(issue.customerName || job?.customerName || "")}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium text-foreground hover:text-primary transition-colors"
                          >
                            {issue.customerName || job?.customerName || "—"}
                          </Link>
                          <Link
                            to={`/jobs/${issue.jobId}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary hover:underline text-[10px] block"
                          >
                            {issue.jobId} · {job?.projectType || ""}
                          </Link>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 hidden lg:table-cell">
                        <span className="text-muted-foreground text-[11px] max-w-[180px] truncate block">
                          {job ? formatAddress(job.address) : "—"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-muted-foreground">{issue.issueType}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1">
                          <SevIcon className={`h-3 w-3 ${issue.severity === "Urgent" ? "text-destructive" : issue.severity === "Medium" ? "text-warning" : "text-muted-foreground"}`} />
                          <StatusBadge type="severity" value={issue.severity} />
                        </div>
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell">
                        <span className="text-foreground">{issue.owner}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        <StatusBadge type="issueStatus" value={issue.status} />
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell">
                        <span className={`text-[11px] ${age > 5 && (issue.status === "Open" || issue.status === "In Progress") ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                          {age}d
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {sorted.length === 0 && (
            <p className="text-center text-muted-foreground py-12 text-sm">No issues match your filters.</p>
          )}
        </CardContent>
      </Card>

      {/* Issue Detail Dialog */}
      {selectedIssue && (
        <Dialog open={!!selectedIssue} onOpenChange={() => { setSelectedIssue(null); setNewNote(""); setResolutionNotes(""); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <DialogTitle className="text-lg">{selectedIssue.id}</DialogTitle>
                <StatusBadge type="severity" value={selectedIssue.severity} />
                <StatusBadge type="issueStatus" value={selectedIssue.status} />
                {isEscalated(selectedIssue) && (
                  <span className="inline-flex items-center gap-1 rounded bg-destructive/10 border border-destructive/20 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                    <ShieldAlert className="h-3 w-3" /> ESCALATED
                  </span>
                )}
              </div>
              {isEscalated(selectedIssue) && (
                <div className="space-y-2 mt-2">
                  <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3 flex items-start gap-2">
                    <ArrowUpCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-destructive">Management Review Required</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        This urgent issue has been open for {daysAgo(selectedIssue.dateCreated)} days and requires management attention.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-md border border-warning/20 bg-warning/10 dark:bg-warning/5 p-3 flex items-start gap-2">
                    <Mail className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-warning dark:text-warning">Email Notification Queued</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Management and the assigned owner ({selectedIssue.owner}) would be notified via email about this escalation.
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> ops-manager@company.com</span>
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {selectedIssue.owner.toLowerCase().replace(/\s/g, '.')}@company.com</span>
                      </div>
                      <p className="text-[9px] text-muted-foreground/60 mt-1 italic">Email integration placeholder — connect email service to activate</p>
                    </div>
                  </div>
                </div>
              )}
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Job Context Banner */}
              {selectedJob && (
                <div className="rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-foreground">{selectedJob.customerName || selectedIssue?.customerName || "—"}</p>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {formatAddress(selectedJob.address)}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {selectedJob.projectType}
                        </span>
                        <StatusBadge type="stage" value={selectedJob.currentStage} />
                        <span className="flex items-center gap-1">
                          {selectedJob.assignmentType === "Subcontractor" ? (
                            <><Users className="h-3 w-3" />{selectedJob.assignedSubcontractor}</>
                          ) : selectedJob.assignmentType === "Internal Crew" ? (
                            <><Shield className="h-3 w-3" />{selectedJob.assignedCrew}</>
                          ) : "Unassigned"}
                        </span>
                        {selectedJob.branch && (
                          <span className="text-muted-foreground">{selectedJob.branch}</span>
                        )}
                      </div>
                    </div>
                    <Link to={`/jobs/${selectedIssue.jobId}`} onClick={() => setSelectedIssue(null)}>
                      <Button variant="outline" size="sm" className="gap-1 text-xs">
                        <ExternalLink className="h-3 w-3" />
                        View Job
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Issue Details Grid */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Issue Details</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium text-foreground">{selectedIssue.issueType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Severity</p>
                    <StatusBadge type="severity" value={selectedIssue.severity} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="font-medium text-foreground">{selectedIssue.source}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Owner</p>
                    <p className="font-medium text-foreground flex items-center gap-1">
                      <User className="h-3 w-3 text-muted-foreground" />
                      {selectedIssue.owner}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Age</p>
                    <p className="font-medium text-foreground">{daysAgo(selectedIssue.dateCreated)} days</p>
                  </div>
                  {selectedContractor && (
                    <div>
                      <p className="text-xs text-muted-foreground">Related Contractor</p>
                      <Link
                        to={`/contractors/${selectedContractor.id}`}
                        onClick={() => setSelectedIssue(null)}
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        <Users className="h-3 w-3" />
                        {selectedContractor.companyName}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Description</h4>
                <p className="text-sm text-foreground leading-relaxed bg-muted/30 rounded-lg p-3 border">
                  {selectedIssue.description}
                </p>
              </div>

              {/* Suggested Next Actions */}
              {(() => {
                const suggestedActions = getIssueSuggestedActions(selectedIssue, selectedJob || undefined);
                if (suggestedActions.length === 0) return null;
                const ACTION_ICONS: Record<string, React.ElementType> = {
                  crew: Users, materials: Package, permit: Shield, schedule: Calendar,
                  issue: Wrench, dispatch: Briefcase, qa: ClipboardCheck, closeout: Clock,
                };
                return (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Zap className="h-3 w-3 text-primary" /> Suggested Next Actions
                    </h4>
                    <div className="space-y-1.5">
                      {suggestedActions.map((action, idx) => {
                        const Icon = ACTION_ICONS[action.icon] || Zap;
                        return (
                          <div
                            key={idx}
                            className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
                              action.priority === "high" ? "border-primary/30 bg-primary/[0.03]" : "border-border bg-muted/20"
                            }`}
                          >
                            <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${action.priority === "high" ? "text-primary" : "text-muted-foreground"}`} />
                            <div className="min-w-0">
                              <p className={`text-xs font-semibold ${action.priority === "high" ? "text-primary" : "text-foreground"}`}>{action.label}</p>
                              <p className="text-[11px] text-muted-foreground">{action.description}</p>
                              {action.link && (
                                <Link to={action.link} onClick={() => setSelectedIssue(null)} className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5 mt-0.5">
                                  Go to module <ArrowRight className="h-2.5 w-2.5" />
                                </Link>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {selectedIssue.photo && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Photo Evidence</h4>
                  <div className="flex gap-2">
                    <a
                      href={selectedIssue.photo}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg border bg-muted/30 overflow-hidden block w-32 h-32 shrink-0"
                    >
                      <img
                        src={selectedIssue.photo}
                        alt="Issue photo evidence"
                        className="h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = "none";
                          const parent = target.parentElement;
                          if (parent) {
                            parent.classList.add("flex", "flex-col", "items-center", "justify-center", "gap-1", "text-muted-foreground", "p-4");
                            parent.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg><span style="font-size:10px">No preview</span>';
                          }
                        }}
                      />
                    </a>
                  </div>
                </div>
              )}

              {/* Lifecycle Timeline */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Issue Lifecycle</h4>

                {/* Status Progress Bar */}
                <div className="flex items-center gap-1 mb-4">
                  {STATUS_OPTIONS.map((s, idx) => {
                    const statusIdx = STATUS_OPTIONS.indexOf(selectedIssue.status);
                    const isActive = idx <= statusIdx;
                    const isCurrent = s === selectedIssue.status;
                    const meta = STATUS_META[s];
                    return (
                      <div key={s} className="flex items-center gap-1 flex-1">
                        <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] font-medium w-full justify-center border ${
                          isCurrent
                            ? "bg-primary/10 border-primary/30 text-primary"
                            : isActive
                            ? "bg-muted border-border text-foreground"
                            : "bg-background border-border text-muted-foreground"
                        }`}>
                          <meta.icon className="h-3 w-3" />
                          {s}
                        </div>
                        {idx < STATUS_OPTIONS.length - 1 && (
                          <ArrowRight className={`h-3 w-3 shrink-0 ${isActive ? "text-primary" : "text-muted-foreground/30"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Timeline Events */}
                <div className="space-y-0 border rounded-lg p-3 bg-muted/20">
                  {buildTimeline(selectedIssue).map((event, idx, arr) => (
                    <div key={idx} className="flex gap-3 py-2">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${event.color} bg-background`}>
                          <event.icon className="h-3.5 w-3.5" />
                        </div>
                        {idx < arr.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-2 pt-0.5">
                        <p className="text-sm font-medium text-foreground">{event.label}</p>
                        <p className="text-xs text-muted-foreground">{event.date}</p>
                        {event.detail && (
                          <p className="text-xs text-muted-foreground mt-0.5 italic">{event.detail}</p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Pending next step indicator */}
                  {selectedIssue.status !== "Closed" && (
                    <div className="flex gap-3 py-2">
                      <div className="flex flex-col items-center">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center border border-dashed border-muted-foreground/40 bg-background">
                          <CircleDot className="h-3.5 w-3.5 text-muted-foreground/40" />
                        </div>
                      </div>
                      <div className="pb-2 pt-0.5">
                        <p className="text-sm text-muted-foreground italic">
                          {selectedIssue.status === "Open" && "Awaiting investigation..."}
                          {selectedIssue.status === "In Progress" && "Awaiting resolution..."}
                          {selectedIssue.status === "Resolved" && "Awaiting final closure..."}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Credits linked to this issue */}
              {(() => {
                const linkedCredits = credits.filter((c) => c.issueId === selectedIssue.id);
                const selectedJob = jobs.find((j) => j.id === selectedIssue.jobId);
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <CreditIcon className="h-3 w-3" /> Customer Credits ({linkedCredits.length})
                      </h4>
                      {!showCreditForm && (
                        <Button size="sm" variant="outline" className="text-xs gap-1 h-6" onClick={() => setShowCreditForm(true)}>
                          <CreditIcon className="h-3 w-3" /> Add Credit
                        </Button>
                      )}
                    </div>
                    {linkedCredits.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {linkedCredits.map((cr) => (
                          <div key={cr.id} className="rounded-lg border bg-muted/20 p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold">{cr.creditType}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-primary">${cr.creditAmount.toLocaleString()}</span>
                                <StatusBadge type="creditStatus" value={cr.status} />
                              </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground">{cr.reasonCategory} · {cr.notes}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">Created {cr.dateCreated} by {cr.createdBy}{cr.dateOffered ? ` · Offered ${cr.dateOffered}` : ""}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCreditForm && selectedJob && (
                      <CreateCreditForm
                        jobId={selectedIssue.jobId}
                        customerId={selectedJob.customerName}
                        issueId={selectedIssue.id}
                        crewName={selectedJob.assignmentType === "Internal Crew" ? selectedJob.assignedCrew : undefined}
                        subcontractorId={selectedJob.assignmentType === "Subcontractor" ? selectedJob.assignedSubcontractor : undefined}
                        onSubmit={(credit: CustomerCredit) => {
                          setCredits((prev) => [...prev, credit]);
                          setShowCreditForm(false);
                        }}
                        onCancel={() => setShowCreditForm(false)}
                      />
                    )}
                  </div>
                );
              })()}

              {/* Resolution Notes */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Resolution Notes</h4>
                {selectedIssue.resolutionNotes && (
                  <div className="bg-success/10 dark:bg-success/20 border border-success/30 dark:border-success/80 rounded-lg p-3 mb-2">
                    <p className="text-sm text-foreground leading-relaxed">{selectedIssue.resolutionNotes}</p>
                    {selectedIssue.dateResolved && (
                      <p className="text-xs text-muted-foreground mt-1">Resolved on {selectedIssue.dateResolved}</p>
                    )}
                  </div>
                )}
                <Textarea
                  placeholder="Add or update resolution notes..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  className="text-sm min-h-[60px]"
                  rows={2}
                />
                {resolutionNotes.trim() && resolutionNotes !== selectedIssue.resolutionNotes && (
                  <PermissionGate permission="issues_edit">
                    <Button size="sm" className="mt-2 gap-1 text-xs" onClick={handleSaveResolutionNotes}>
                      <CheckCircle2 className="h-3 w-3" />
                      Save Resolution Notes
                    </Button>
                  </PermissionGate>
                )}
              </div>

              {/* Status Update */}
              <div className="border-t pt-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Update Status</h4>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_OPTIONS.map((s) => {
                    const meta = STATUS_META[s];
                    return (
                      <PermissionGate key={s} permission="issues_edit">
                        <Button
                          variant={selectedIssue.status === s ? "default" : "outline"}
                          size="sm"
                          className="text-xs gap-1"
                          onClick={() => handleStatusChange(selectedIssue.id, s)}
                        >
                          <meta.icon className="h-3 w-3" />
                          {s}
                        </Button>
                      </PermissionGate>
                    );
                  })}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}